/*
 * 0.87: Kleine Diagramme für Statistik-Bausteine – eine Achse, dünne Marken, Werte direkt beschriftet,
 * Legende ab zwei Reihen, Tooltip per <title>, dazu immer „Als Tabelle“. Farben nur über CSS-Variablen
 * (--chart-1…3, --cat-1…8; hell/dunkel in index.css, geprüft mit dem Farbvalidator).
 */
import type { ReactNode } from 'react';
import { cx } from '../ui/ui';

/** Kategorien in fester Reihenfolge; ab der neunten Gruppe „Weitere“ (grau) – nie zyklisch */
export const catColor = (i: number) => (i < 8 ? `var(--cat-${i + 1})` : 'var(--cat-other)');

/** Hex der Labelfarben (wie LABEL_BG) für SVG */
export const LABEL_HEX: Record<string, string> = {
  green: '#10b981',
  yellow: '#fbbf24',
  orange: '#f97316',
  red: '#f43f5e',
  purple: '#8b5cf6',
  blue: '#3b82f6',
  sky: '#38bdf8',
  lime: '#84cc16',
  pink: '#ec4899',
  gray: '#94a3b8',
};

export interface BarRow {
  key: string;
  label: string;
  value: number;
  text: string;
  color?: string;
  sub?: string;
}

/** Tabelle zum Diagramm (aufklappbar) */
export function DataTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <details className="mt-1 text-xs">
      <summary className="cursor-pointer text-[var(--ink-soft)]">Als Tabelle</summary>
      <table className="mt-1 w-full text-left">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} className="pr-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((v, j) => (
                <td key={j} className={cx('pr-2', j > 0 && 'tabular-nums')}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/** Waagerechte Balkenliste; Zeilen optional anklickbar (Trefferliste) */
export function BarList({ rows, onPick, max: fixedMax }: { rows: BarRow[]; onPick?: (r: BarRow) => void; max?: number }) {
  const max = fixedMax ?? Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  return (
    <ul className="flex flex-col gap-0.5">
      {rows.map((r) => {
        const inner = (
          <>
            <span className="min-w-0 truncate text-left" title={r.label}>
              {r.color && <span aria-hidden className="mr-1.5 inline-block size-2.5 rounded-sm align-middle" style={{ background: r.color }} />}
              {r.label}
              {r.sub && <span className="block truncate text-xs text-[var(--ink-soft)]">{r.sub}</span>}
            </span>
            <span className="h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.round((Math.abs(r.value) / max) * 100)}%`,
                  background: r.color ?? 'var(--chart-1)',
                }}
              />
            </span>
            <span className="text-right whitespace-nowrap tabular-nums">{r.text}</span>
          </>
        );
        const grid = 'grid w-full grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2 text-sm';
        return (
          <li key={r.key}>
            {onPick ? (
              <button
                type="button"
                className={cx(grid, 'min-h-9 rounded-md px-1 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]')}
                onClick={() => onPick(r)}
                aria-label={`${r.label}: ${r.text} – Karten anzeigen`}
              >
                {inner}
              </button>
            ) : (
              <div className={cx(grid, 'min-h-8 px-1')}>{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Ring mit Gesamtzahl in der Mitte; Legende als anklickbare Liste daneben (Identität nie nur über Farbe) */
export function Donut({ rows, total, center, onPick, aria }: { rows: BarRow[]; total: number; center: ReactNode; onPick?: (r: BarRow) => void; aria: string }) {
  const R = 40;
  const C = 2 * Math.PI * R;
  const sum = rows.reduce((a, r) => a + r.value, 0) || 1;
  let acc = 0;
  // 2px Fläche zwischen Segmenten (bei sehr kleinen Segmenten ohne Lücke)
  const gap = rows.length > 1 ? 1.2 : 0;
  return (
    <div className="flex flex-wrap items-center gap-4">
      <figure className="relative size-32 shrink-0">
        <svg viewBox="0 0 100 100" className="size-32 -rotate-90" role="img" aria-label={aria}>
          <circle cx="50" cy="50" r={R} fill="none" stroke="var(--chart-grid)" strokeWidth="14" />
          {rows.map((r) => {
            const len = (r.value / sum) * C;
            const seg = (
              <circle
                key={r.key}
                cx="50"
                cy="50"
                r={R}
                fill="none"
                stroke={r.color ?? 'var(--chart-1)'}
                strokeWidth="14"
                strokeDasharray={`${Math.max(0, len - (len > gap * 2 ? gap : 0))} ${C}`}
                strokeDashoffset={-acc}
              >
                <title>
                  {r.label}: {r.text}
                </title>
              </circle>
            );
            acc += len;
            return seg;
          })}
        </svg>
        <figcaption className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-xl font-semibold tabular-nums">{total}</span>
          <span className="text-[11px] text-[var(--ink-soft)]">{center}</span>
        </figcaption>
      </figure>
      <ul className="flex min-w-40 flex-1 flex-col">
        {rows.map((r) => {
          const inner = (
            <>
              <span aria-hidden className="size-2.5 shrink-0 rounded-sm" style={{ background: r.color ?? 'var(--chart-1)' }} />
              <span className="min-w-0 flex-1 truncate text-left">{r.label}</span>
              <span className="tabular-nums text-[var(--ink-soft)]">{r.text}</span>
            </>
          );
          const cls = 'flex w-full items-center gap-2 text-sm';
          return (
            <li key={r.key}>
              {onPick ? (
                <button
                  type="button"
                  className={cx(cls, 'min-h-9 rounded-md px-1 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]')}
                  onClick={() => onPick(r)}
                  aria-label={`${r.label}: ${r.text} – Karten anzeigen`}
                >
                  {inner}
                </button>
              ) : (
                <div className={cx(cls, 'min-h-8 px-1')}>{inner}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export interface Series {
  name: string;
  color: string;
}

/**
 * Säulen je Zeitabschnitt: nebeneinander (`stacked=false`) oder gestapelt. Angeschnittene (laufende) Abschnitte
 * blasser. Höchstwert als Achsenhinweis, eine Achse.
 */
export function Columns({
  buckets,
  series,
  stacked,
  aria,
  unit,
}: {
  buckets: { label: string; values: number[]; partial?: boolean }[];
  series: Series[];
  stacked?: boolean;
  aria: string;
  unit?: string;
}) {
  const n = buckets.length;
  const max = Math.max(1, ...buckets.map((b) => (stacked ? b.values.reduce((a, v) => a + v, 0) : Math.max(0, ...b.values))));
  const H = 80;
  const slot = 24;
  const inner = stacked ? 14 : Math.min(9, 18 / series.length);
  return (
    <figure className="mt-1">
      {series.length > 1 && (
        <ul className="mb-1 flex flex-wrap gap-x-3 text-xs text-[var(--ink-soft)]" aria-label="Legende">
          {series.map((s) => (
            <li key={s.name} className="flex items-center gap-1">
              <span aria-hidden className="size-2.5 rounded-sm" style={{ background: s.color }} />
              {s.name}
            </li>
          ))}
        </ul>
      )}
      <svg viewBox={`0 0 ${n * slot} ${H + 2}`} className="h-24 w-full" preserveAspectRatio="none" role="img" aria-label={aria}>
        <line x1="0" x2={n * slot} y1={H + 1} y2={H + 1} stroke="var(--chart-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {buckets.map((b, i) => {
          const x0 = i * slot + (slot - (stacked ? inner : inner * series.length + (series.length - 1))) / 2;
          let y = H;
          return (
            <g key={b.label + i} opacity={b.partial ? 0.55 : 1}>
              <title>
                {b.label}
                {b.partial ? ' (laufend)' : ''}: {series.map((s, j) => `${s.name} ${b.values[j]}`).join(', ')}
              </title>
              {b.values.map((v, j) => {
                const h = v ? Math.max(1.5, (v / max) * (H - 2)) : 0;
                if (stacked) {
                  y -= h;
                  // 2px Fläche zwischen gestapelten Teilen
                  return <rect key={j} x={x0} y={y + (j ? 0.75 : 0)} width={inner} height={Math.max(0, h - (j ? 0.75 : 0))} rx="1.5" fill={series[j]!.color} />;
                }
                return <rect key={j} x={x0 + j * (inner + 1)} y={H - h} width={inner} height={h} rx="1.5" fill={series[j]!.color} />;
              })}
            </g>
          );
        })}
      </svg>
      <figcaption className="flex justify-between gap-2 text-xs text-[var(--ink-soft)]">
        <span>{buckets[0]?.label}</span>
        <span>
          höchstens {max}
          {unit ? ` ${unit}` : ''}
        </span>
        <span>{buckets.at(-1)?.label}</span>
      </figcaption>
    </figure>
  );
}

/** Linie für einen Verlauf (eigenes Diagramm, nie zweite Achse) */
export function Line({ points, aria, color = 'var(--chart-3)' }: { points: { label: string; value: number; partial?: boolean }[]; aria: string; color?: string }) {
  const n = points.length;
  const max = Math.max(1, ...points.map((p) => p.value));
  const min = Math.min(0, ...points.map((p) => p.value));
  const H = 44;
  const W = Math.max(1, n - 1) * 24;
  const y = (v: number) => (max === min ? H / 2 : H - 3 - ((v - min) / (max - min)) * (H - 6));
  const x = (i: number) => (n === 1 ? W / 2 : i * 24);
  return (
    <figure className="mt-1">
      <svg viewBox={`-4 0 ${W + 8} ${H}`} className="h-12 w-full" preserveAspectRatio="none" role="img" aria-label={aria}>
        <polyline
          points={points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Unsichtbare Trefferflächen je Punkt für den Tooltip */}
        {points.map((p, i) => (
          <rect key={i} x={x(i) - 12} y="0" width="24" height={H} fill="transparent">
            <title>
              {p.label}: {p.value}
            </title>
          </rect>
        ))}
      </svg>
      <figcaption className="flex justify-between text-xs text-[var(--ink-soft)]">
        <span>{points[0] ? `${points[0].label}: ${points[0].value}` : ''}</span>
        <span>{points.at(-1) ? `${points.at(-1)!.label}: ${points.at(-1)!.value}` : ''}</span>
      </figcaption>
    </figure>
  );
}
